import { useEffect, useRef, useState } from 'react'
import {
  MONO_STACK,
  diffChars,
  encodeChar,
  layoutTokens,
  toChars,
} from './messageTerminalLogic.js'
import './message-terminal.css'

const LINE_H = 20
const PAD_X = 18
const TOP = 18
const SAMPLE = '01001000'
const BINARY_COLOR = '#c1c1c1'
const CHAR_COLOR = '#ececec'
const METER_SLOTS = 16
const PASTE_THRESHOLD = 36

function fadeAlpha(baseline, overflowing) {
  if (!overflowing) return 1
  const top = baseline - 12
  const zone = 58
  if (top >= zone) return 1
  if (top <= 0) return 0
  const t = top / zone
  return t * t * (3 - 2 * t)
}

function smoothstep(t, from, to) {
  if (t <= from) return 0
  if (t >= to) return 1
  const x = (t - from) / (to - from)
  return x * x * (3 - 2 * x)
}

function paintMeter(meterEl, pctEl, progress) {
  if (!meterEl) return
  const clamped = Math.max(0, Math.min(1, progress))
  const filled = Math.round(clamped * METER_SLOTS)
  meterEl.textContent = `${'█'.repeat(filled)}${'·'.repeat(METER_SLOTS - filled)}`
  if (pctEl) pctEl.textContent = `${Math.round(clamped * 100)}%`
}

function createEngine(canvas, handlersRef) {
  const ctx = canvas.getContext('2d', { alpha: true })
  if (!ctx) {
    return { setText() {}, resize() {}, destroy() {} }
  }
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  let width = 0
  let height = 0
  let dpr = 1
  let tokenWidth = 62
  let spaceWidth = 4
  let prev = ''
  let committed = []
  let queue = []
  let active = null
  let scroll = 0
  let scrollTarget = 0
  let linesCache = null
  let raf = 0
  let last = 0
  let destroyed = false

  function contentWidth() {
    return Math.max(24, width - PAD_X * 2)
  }

  function flatten(groups) {
    const tokens = []
    for (const group of groups) {
      for (const bits of group.bits) tokens.push(bits)
    }
    return tokens
  }

  function linesOf(groups) {
    return layoutTokens(flatten(groups), contentWidth(), tokenWidth, spaceWidth)
  }

  function getLines() {
    if (!linesCache) linesCache = linesOf(committed)
    return linesCache
  }

  function contentBottom() {
    return Math.max(TOP + LINE_H, height - 4)
  }

  function scrollTargetFor(lineCount) {
    const span = Math.max(0, contentBottom() - TOP)
    const content = Math.max(0, (lineCount - 1) * LINE_H)
    return Math.max(0, content - span)
  }

  function placeScroll(lineCount) {
    scrollTarget = scrollTargetFor(lineCount)
  }

  function trim() {
    let lines = getLines()
    const limit = Math.max(4, Math.floor((contentBottom() - TOP) / LINE_H) + 3)
    let guard = 0
    while ((lines.length > limit || committed.length > 220) && committed.length > 1 && guard < 300) {
      const before = lines.length
      committed.shift()
      linesCache = null
      lines = getLines()
      const dropped = before - lines.length
      if (dropped > 0) {
        const px = dropped * LINE_H
        scroll = Math.max(0, scroll - px)
        scrollTarget = Math.max(0, scrollTarget - px)
      }
      guard += 1
    }
    placeScroll(lines.length)
  }

  function commitGroup(group) {
    committed.push(group)
    linesCache = null
    trim()
    placeScroll(getLines().length)
  }

  function resync(next, morphLast) {
    const chars = toChars(next).slice(-32)
    queue = []
    active = null
    const seed = morphLast && chars.length ? chars.slice(0, -1) : chars
    committed = seed.map((ch) => ({ bits: encodeChar(ch) }))
    linesCache = null
    trim()
    placeScroll(getLines().length)
    scroll = reduceMotion ? scrollTarget : Math.max(0, scrollTarget - 2 * LINE_H)
    if (!morphLast || !chars.length) return
    const lastChar = chars[chars.length - 1]
    if (reduceMotion) commitGroup({ bits: encodeChar(lastChar) })
    else queue.push(lastChar)
  }

  function dropFromTail(count) {
    let left = count
    while (left > 0 && queue.length) {
      queue.pop()
      left -= 1
    }
    if (left > 0 && active) {
      active = null
      left -= 1
    }
    if (left > 0 && committed.length) {
      committed.splice(Math.max(0, committed.length - left), left)
      linesCache = null
      placeScroll(getLines().length)
    }
  }

  function lineBaseline(lineIndex) {
    return TOP + lineIndex * LINE_H - scroll
  }

  function isOverflowing() {
    return scrollTarget > 0.5 || scroll > 0.5
  }

  function activeExtraLines() {
    if (!active || !getLines().length) return 0
    const last = getLines()[getLines().length - 1]
    const used = last.length * tokenWidth + Math.max(0, last.length - 1) * spaceWidth
    const gap = last.length ? spaceWidth : 0
    return used + gap + tokenWidth <= contentWidth() ? 0 : 1
  }

  function drawActive(lines) {
    const lineCount = lines.length
    const t = Math.max(0, Math.min(1, (performance.now() - active.started) / active.duration))
    const charAlpha = 1 - smoothstep(t, 0.2, 0.72)
    const bitAlpha = smoothstep(t, 0.34, 0.92)
    const slots = []
    let lineIndex = 0
    let x = PAD_X

    if (lineCount === 0) {
      lineIndex = 0
      x = PAD_X
    } else {
      const last = lines[lineCount - 1]
      const used = last.length * tokenWidth + Math.max(0, last.length - 1) * spaceWidth
      const gap = last.length ? spaceWidth : 0
      if (used + gap + tokenWidth <= contentWidth()) {
        lineIndex = lineCount - 1
        x = PAD_X + used + gap
      } else {
        lineIndex = lineCount
        x = PAD_X
      }
    }

    for (const bit of active.bits) {
      if (x > PAD_X && x + tokenWidth > PAD_X + contentWidth()) {
        lineIndex += 1
        x = PAD_X
      }
      slots.push({ bit, x, y: lineBaseline(lineIndex) })
      x += tokenWidth + spaceWidth
    }

    const first = slots[0]
    if (first && charAlpha > 0.02) {
      ctx.save()
      ctx.globalAlpha = charAlpha * fadeAlpha(first.y, isOverflowing())
      ctx.fillStyle = CHAR_COLOR
      ctx.translate(first.x, first.y - (1 - charAlpha) * 2)
      ctx.scale(1 + t * 0.42, 1)
      ctx.fillText(active.char, 0, 0)
      ctx.restore()
    }

    ctx.fillStyle = BINARY_COLOR
    for (const slot of slots) {
      const alpha = bitAlpha * fadeAlpha(slot.y, isOverflowing())
      if (alpha < 0.02) continue
      ctx.globalAlpha = alpha
      ctx.fillText(slot.bit, slot.x, slot.y)
    }
  }

  function draw() {
    if (!ctx || width < 2 || height < 2) return
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, width, height)
    ctx.font = `13px ${MONO_STACK}`
    ctx.textBaseline = 'alphabetic'
    ctx.textAlign = 'left'
    ctx.fillStyle = BINARY_COLOR

    const lines = getLines()
    for (let i = 0; i < lines.length; i += 1) {
      const y = lineBaseline(i)
      const alpha = fadeAlpha(y, isOverflowing())
      if (alpha < 0.02) continue
      ctx.globalAlpha = alpha
      ctx.fillText(lines[i].join(' '), PAD_X, y)
    }

    if (active) drawActive(lines)
    ctx.globalAlpha = 1
  }

  function frame(now) {
    raf = 0
    if (destroyed) return
    const dt = Math.min(34, now - last || 16)
    last = now

    if (!active && queue.length > 28) {
      const take = Math.min(24, queue.length - 1)
      const chunk = queue.splice(0, take)
      for (const ch of chunk) commitGroup({ bits: encodeChar(ch) })
    }

    if (!active && queue.length) {
      const ch = queue.shift()
      if (reduceMotion) {
        commitGroup({ bits: encodeChar(ch) })
      } else {
        const pressure = queue.length
        active = {
          char: ch,
          bits: encodeChar(ch),
          started: now,
          duration: pressure > 8 ? 64 : 108 + ((ch.codePointAt(0) || 0) % 40),
        }
      }
    }

    if (active && (now - active.started) / active.duration >= 1) {
      const bits = active.bits
      active = null
      commitGroup({ bits })
    }

    const goal = scrollTargetFor(getLines().length + activeExtraLines())
    if (reduceMotion) {
      scroll = goal
    } else {
      const ease = 1 - Math.exp(-dt / 130)
      scroll += (goal - scroll) * ease
      if (Math.abs(goal - scroll) < 0.05) scroll = goal
    }

    draw()

    if (queue.length || active || Math.abs(scroll - scrollTarget) > 0.05) raf = requestAnimationFrame(frame)
  }

  function kick() {
    if (destroyed || raf) return
    last = performance.now()
    raf = requestAnimationFrame(frame)
  }

  function setText(next) {
    const value = next || ''
    if (value === prev) return
    const previous = prev
    prev = value

    if (!value) {
      committed = []
      queue = []
      active = null
      scroll = 0
      scrollTarget = 0
      linesCache = null
      handlersRef.current.onEmpty?.()
      draw()
      return
    }

    const { added, removed } = diffChars(previous, value)
    if (!added.length && !removed) return

    if (added.length > PASTE_THRESHOLD || removed > PASTE_THRESHOLD) {
      resync(value, added.length > 0)
    } else {
      if (removed) dropFromTail(removed)
      if (added.length) queue.push(...added)
      if (!queue.length && !active && committed.length === 0) resync(value, false)
    }

    handlersRef.current.onType?.()
    if (queue.length || active || Math.abs(scroll - scrollTarget) > 0.05) kick()
    else draw()
  }

  function resize() {
    const rect = canvas.getBoundingClientRect()
    dpr = Math.min(window.devicePixelRatio || 1, 2)
    width = rect.width
    height = rect.height
    canvas.width = Math.max(1, Math.floor(width * dpr))
    canvas.height = Math.max(1, Math.floor(height * dpr))
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.font = `13px ${MONO_STACK}`
    tokenWidth = ctx.measureText(SAMPLE).width
    spaceWidth = ctx.measureText(' ').width
    linesCache = null
    trim()
    draw()
    if (queue.length || active || Math.abs(scroll - scrollTarget) > 0.05) kick()
  }

  function destroy() {
    destroyed = true
    if (raf) cancelAnimationFrame(raf)
    raf = 0
  }

  return { setText, resize, destroy }
}

export default function MessageTerminal({ text, phase }) {
  const canvasRef = useRef(null)
  const streamRef = useRef(null)
  const engineRef = useRef(null)
  const handlersRef = useRef({})
  const phaseRef = useRef(phase)
  const meterRef = useRef(null)
  const pctRef = useRef(null)
  const introTimer = useRef(0)
  const pauseTimer = useRef(0)
  const [mode, setMode] = useState('idle')
  const [intro, setIntro] = useState('visible')

  useEffect(() => {
    phaseRef.current = phase
    handlersRef.current = {
      onEmpty() {
        window.clearTimeout(introTimer.current)
        window.clearTimeout(pauseTimer.current)
        setIntro('visible')
        setMode('idle')
      },
      onType() {
        setMode('live')
        setIntro((current) => {
          if (current !== 'visible') return current
          window.clearTimeout(introTimer.current)
          introTimer.current = window.setTimeout(() => setIntro('hidden'), 460)
          return 'leaving'
        })
        window.clearTimeout(pauseTimer.current)
        pauseTimer.current = window.setTimeout(() => {
          if (phaseRef.current === 'idle') setMode('paused')
        }, 2400)
      },
    }
  })

  useEffect(() => {
    const canvas = canvasRef.current
    const stream = streamRef.current
    if (!canvas || !stream) return undefined
    const engine = createEngine(canvas, handlersRef)
    engineRef.current = engine
    const observer = new ResizeObserver(() => engine.resize())
    observer.observe(stream)
    engine.resize()
    return () => {
      observer.disconnect()
      engine.destroy()
      if (engineRef.current === engine) engineRef.current = null
    }
  }, [])

  useEffect(() => {
    engineRef.current?.setText(text)
  }, [text])

  useEffect(() => {
    return () => {
      window.clearTimeout(introTimer.current)
      window.clearTimeout(pauseTimer.current)
    }
  }, [])

  useEffect(() => {
    if (phase !== 'transmitting') return undefined
    const started = performance.now()
    let raf = 0
    const tick = (now) => {
      const t = Math.min(1, (now - started) / 980)
      const eased = 1 - (1 - t) ** 3
      paintMeter(meterRef.current, pctRef.current, eased * 0.9)
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [phase])

  useEffect(() => {
    if (phase === 'delivered') paintMeter(meterRef.current, pctRef.current, 1)
  }, [phase])

  const showTransmit = phase === 'transmitting' || phase === 'delivered'

  return (
    <div className="message-terminal" aria-hidden="true">
      <div className="message-terminal__stream" ref={streamRef}>
        <canvas ref={canvasRef} />
        {intro !== 'hidden' && (
          <div className={`mt-intro${intro === 'leaving' ? ' is-leaving' : ''}`}>
            <p className="mt-intro__prompt">&gt; waiting...</p>
            <p>start typing in the form.</p>
            <p>this side turns it into bits.</p>
          </div>
        )}
      </div>
      <div className="message-terminal__status">
        {showTransmit && (
          <div className="mt-seq">
            <div>&gt; transmitting...</div>
            <div className="mt-seq__line--meter">
              &gt; <span ref={meterRef}>{'·'.repeat(METER_SLOTS)}</span> <span ref={pctRef}>0%</span>
            </div>
            {phase === 'delivered' && <div className="mt-seq__line--done">&gt; message delivered ✓</div>}
          </div>
        )}
        {phase === 'error' && (
          <div className="mt-seq">
            <div>&gt; transmission failed</div>
          </div>
        )}
        {phase === 'idle' && mode === 'paused' && <div>&gt; waiting...</div>}
      </div>
    </div>
  )
}
